import { createApi } from '@reduxjs/toolkit/query/react';

import { Customer } from '@/types/Customer';
import { ResponseError } from '@/types/Error';
import { SuccessResponse } from '@/types/Success';

import { generateBaseQuseryWithReAuth } from '../baseQueryReauth';
import { setAuthenticationLoadingStatus, setAuthenticationStatus } from '../slices/authentication';

export const customerApi = createApi({
  reducerPath: 'customerApi',
  keepUnusedDataFor: 300,
  baseQuery: generateBaseQuseryWithReAuth({ basePath: `User` }),
  tagTypes: ['Customers', 'checkIfUserAuthenticated'],

  endpoints: builder => ({
    getCustomer: builder.query<SuccessResponse<Customer>, void>({
      query: () => ({
        url: '/GetCustomer',
        next: { tags: ['Customers'] },
      }),
      transformResponse: (response: SuccessResponse<Customer>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
      providesTags: ['Customers'],
      // eslint-disable-next-line sonarjs/cognitive-complexity
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        dispatch(setAuthenticationLoadingStatus(true));

        try {
          await queryFulfilled;
        } catch (error) {
          console.group('[GetCustomer] Query errors');
          console.warn('[GetCustomer] Query failed:', error);

          // eslint-disable-next-line sonarjs/cognitive-complexity
          const redirectIfPrivateRoute = async () => {
            try {
              let isPrivate = false;
              console.warn('[GetCustomer] Importing utils to check if the current route is private');
              const [
                { isRegExp },
                { getValidSubdomain, generateApproperiateURL, getWebaliasAlongWithRestPath },
                { PRIVATE_ROUTES },
                { pathToRegexp },
              ] = await Promise.all([
                import('@/helpers/utils/typeGaurds'),
                import('@/helpers/utils/urlHelpers'),
                import('@/config/routes'),
                import('path-to-regexp'),
              ]);

              console.warn("[GetCustomer] Checking if it's backoffice ");
              const subdomain = getValidSubdomain(globalThis?.window?.location?.hostname);

              const isBackOffice = subdomain === 'backoffice' || subdomain?.includes('backoffice');

              if (isBackOffice) console.warn("[GetCustomer] yes, it is backoffice user's request");

              const urlToTest = globalThis?.window?.location?.pathname?.split('?')[0] || '';

              console.warn('[GetCustomer] Testing the route against private routes list', urlToTest);
              isPrivate = PRIVATE_ROUTES.map(route => {
                try {
                  return pathToRegexp(route);
                } catch (err) {
                  return route;
                }
              }).some(rx => (isRegExp(rx) ? rx.test(urlToTest) : rx === urlToTest));
              const returnable = !!(isPrivate || isBackOffice);

              const finalPrivate = returnable;

              console.warn('[GetCustomer] isPrivateRoute:', finalPrivate);
              if (finalPrivate) {
                let WEBALIAS_PATH = '';

                if (!isBackOffice) {
                  const { webalias: raw_webalias, urlPath } = getWebaliasAlongWithRestPath(
                    globalThis?.window?.location?.pathname || ''
                  );

                  const webalias = raw_webalias?.split('?')[0];
                  WEBALIAS_PATH = webalias || 'pawtree';

                  console.warn('[GetCustomer] Webalias:', WEBALIAS_PATH);
                }

                const logoutPath = isBackOffice ? '/logout' : `/${WEBALIAS_PATH}/account/logout`;
                const clearCookiesPath = `/api/clear-user-cookies`;
                try {
                  console.warn('[GetCustomer] Clearing authentication cookies through logout endpoint');
                  const logoutTheUser = await fetch(logoutPath, {
                    method: 'GET',
                    credentials: 'same-origin',
                  });
                  await logoutTheUser.text();
                  console.warn('[GetCustomer] Clearing rest of the cookies');
                  const clearCookies = await fetch(clearCookiesPath, {
                    method: 'GET',
                    credentials: 'same-origin',
                  });
                  await clearCookies.text();
                  alert("We're having trouble authenticating. Please log in again.");
                } catch (error) {
                  console.error(
                    '[GetCustomer] error while logging out the user - on failure from backend GetCustomer',
                    error
                  );
                }

                if (isBackOffice) {
                  console.warn('[GetCustomer] Redirecting to backoffice login');
                  window.location.href = `/login?ReturnUrl=${encodeURIComponent(window.location.pathname)}`;
                } else {
                  const isGuestUserFlow =
                    typeof window !== 'undefined' &&
                    new URLSearchParams(window.location.search).get('isGuestUser') === 'true';

                  if (isGuestUserFlow) {
                    const currentUrl = new URL(window.location.href);
                    currentUrl.searchParams.delete('isGuestUser');
                    const cleanReturnUrl = currentUrl.toString();
                    console.warn('[GetCustomer] Redirecting to login - guest user flow');
                    window.location.href = generateApproperiateURL(
                      `/account/login?tab=signup&ReturnUrl=${encodeURIComponent(cleanReturnUrl)}`,
                      WEBALIAS_PATH
                    );
                  } else {
                    console.warn('[GetCustomer] Redirecting to login');
                    window.location.href = generateApproperiateURL(
                      `/account/login/?ReturnUrl=${encodeURIComponent(window.location.href)}`,
                      WEBALIAS_PATH
                    );
                  }
                }
              }
            } catch (err) {
              console.error('[GetCustomer] redirectIfPrivateRoute error in customerAPI', err);
            }
          };
          console.warn('[GetCustomer] Setting AuthenticationStatus to false');
          dispatch(setAuthenticationStatus(false));
          await redirectIfPrivateRoute();
          console.groupEnd();
        } finally {
          console.warn('[GetCustomer] Setting Authentication Loading Status to false');
          dispatch(setAuthenticationLoadingStatus(false));
        }
      },
    }),
    checkIfUserAuthenticated: builder.query<SuccessResponse<unknown>, void>({
      query: () => ({
        url: '/CheckIfUserAuthenticated',
        next: { tags: ['checkIfUserAuthenticated'] },
      }),
      transformResponse: (response: SuccessResponse<unknown>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
      providesTags: ['checkIfUserAuthenticated'],
    }),
  }),
});

export const { useGetCustomerQuery, useCheckIfUserAuthenticatedQuery } = customerApi;
